import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useAdminContext } from '../contexts/AdminContextProvider';
import TenantContextDropdown from '../components/common/TenantContextDropdown';
import { isSuperAdmin } from '../components/common/UserMenu';
import MicrosoftGraphConfigCard from '../components/sso/MicrosoftGraphConfigCard';
import {
  RefreshIcon,
  KeyIcon,
  CheckCircleIcon,
  PlayIcon,
  SettingsIcon,
  BuildingIcon,
  AlertIcon,
  PlusIcon,
  CheckIcon,
  TrashIcon
} from '../components/common/Icons';

interface SSOProvider {
  id: string;
  name: string;
  description: string;
  discovery_supported: boolean;
}

interface SSOProviderConfig {
  name: string;
  enabled: boolean;
  protocol: string;
  tenant_id?: string;
}

interface SSOStatus {
  enabled: boolean;
  provider: string | null;
  allow_local_auth: boolean;
  force_sso: boolean;
  provider_count?: number;
  active_sessions?: number;
}

interface SSOConfig {
  enabled: boolean;
  provider: string;
  issuer_url: string;
  client_id: string;
  client_secret: string;
  redirect_uri: string;
  auto_create_users: boolean;
  default_role: string;
  allow_local_auth: boolean;
  force_sso: boolean;
  role_mapping: Record<string, string>;
}

interface TestResult {
  success: boolean;
  checks: Record<string, { success: boolean; error?: string; endpoints_found?: number; keys_found?: number }>;
}

// Available platform roles for role mapping
const PLATFORM_ROLES = ['superadmin', 'admin', 'tenant_admin', 'operator', 'user'];

const SSOPage: React.FC = () => {
  const { authFetch, isLoading: authLoading, user } = useAuth();
  const { activeTenant, isInTenantContext } = useAdminContext();

  // Data state
  const [providers, setProviders] = useState<SSOProvider[]>([]);
  const [, setConfiguredProviders] = useState<SSOProviderConfig[]>([]);
  const [status, setStatus] = useState<SSOStatus | null>(null);
  const [, setConfig] = useState<SSOConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Test Connection state
  const [testingProvider, setTestingProvider] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [showTestModal, setShowTestModal] = useState(false);

  // Add Provider Modal state
  const [showAddProviderModal, setShowAddProviderModal] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [providerForm, setProviderForm] = useState({
    provider: 'keycloak',
    issuer_url: '',
    client_id: '',
    client_secret: '',
    redirect_uri: window.location.origin + '/api/auth/sso/callback',
    auto_create_users: true,
    default_role: 'user',
    allow_local_auth: true,
    force_sso: false,
  });

  // Role Mapping Modal state
  const [showRoleMappingModal, setShowRoleMappingModal] = useState(false);
  const [isSavingRoleMapping, setIsSavingRoleMapping] = useState(false);
  const [roleMappingError, setRoleMappingError] = useState<string | null>(null);
  const [roleMapping, setRoleMapping] = useState<Array<{ ssoRole: string; platformRole: string }>>([
    { ssoRole: '', platformRole: 'user' }
  ]);

  // Disable SSO state
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  // Success feedback state
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Domain Management state (WQ-071)
  interface DomainMapping {
    id: string;
    tenant_id: string;
    domain: string;
    verified: boolean;
    verification_method: string | null;
    verified_at: string | null;
    verified_by: string | null;
    is_primary: boolean;
    created_at: string | null;
  }
  const [domains, setDomains] = useState<DomainMapping[]>([]);
  const [showAddDomainModal, setShowAddDomainModal] = useState(false);
  const [newDomainName, setNewDomainName] = useState('');
  const [domainError, setDomainError] = useState<string | null>(null);

  // Routing strategy:
  // - Super admin in tenant context → admin endpoints + ?tenant_id=
  // - Tenant admin (JWT has tenant_id) → tenant portal endpoints
  // - Platform level (no tenant context) → admin endpoints (no tenant_id)
  const userIsSuperAdmin = user?.role ? isSuperAdmin(user.role) : false;
  const useAdminWithTenantScope = isInTenantContext && userIsSuperAdmin;

  const tenantScopeParam = useAdminWithTenantScope && activeTenant
    ? `?tenant_id=${activeTenant.id}`
    : '';

  // Context-aware API endpoints
  const ssoConfigEndpoint = (isInTenantContext && !userIsSuperAdmin)
    ? '/api/tenant-portal/sso/config'
    : `/api/admin/sso/config${tenantScopeParam}`;

  const ssoTestEndpoint = (isInTenantContext && !userIsSuperAdmin)
    ? '/api/tenant-portal/sso/test'
    : `/api/admin/sso/test${tenantScopeParam}`;

  const ssoStatusEndpoint = isInTenantContext && activeTenant
    ? `/api/auth/sso/status?tenant=${activeTenant.slug}`
    : '/api/auth/sso/status';

  const ssoRoleMappingEndpoint = (isInTenantContext && !userIsSuperAdmin)
    ? '/api/tenant-portal/sso/role-mapping'
    : `/api/admin/sso/role-mapping${tenantScopeParam}`;

  const ssoDisableEndpoint = (isInTenantContext && !userIsSuperAdmin)
    ? '/api/tenant-portal/sso/disable'
    : `/api/admin/sso/disable${tenantScopeParam}`;

  const ssoDomainsEndpoint = (isInTenantContext && !userIsSuperAdmin)
    ? '/api/tenant-portal/sso/domains'
    : `/api/admin/sso/domains${tenantScopeParam}`;

  // Fetch SSO data
  const fetchSSOData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [providersRes, statusRes, configRes] = await Promise.all([
        authFetch('/api/auth/sso/providers'),
        authFetch(ssoStatusEndpoint),
        authFetch(ssoConfigEndpoint).catch(() => null), // May fail if no permission
      ]);

      if (providersRes.ok) {
        const data = await providersRes.json();
        setProviders(data.providers || []);
        // Create configured providers list from status
        if (data.providers) {
          setConfiguredProviders(data.providers.map((p: SSOProvider) => ({
            name: p.name,
            enabled: false,
            protocol: 'OIDC',
          })));
        }
      } else {
        const errorData = await providersRes.json().catch(() => ({}));
        setError(errorData.detail || 'Failed to fetch SSO providers');
      }

      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setStatus(statusData);
        // Update configured providers based on status
        if (statusData.enabled && statusData.provider) {
          setConfiguredProviders(prev => prev.map(p =>
            p.name.toLowerCase() === statusData.provider?.toLowerCase()
              ? { ...p, enabled: true }
              : p
          ));
        }
      }

      if (configRes && configRes.ok) {
        const configData = await configRes.json();
        setConfig(configData);
        // Initialize role mapping from config
        if (configData.role_mapping && Object.keys(configData.role_mapping).length > 0) {
          setRoleMapping(
            Object.entries(configData.role_mapping).map(([ssoRole, platformRole]) => ({
              ssoRole,
              platformRole: platformRole as string,
            }))
          );
        }
      }

      // Fetch domain mappings (WQ-071)
      try {
        const domainsRes = await authFetch(ssoDomainsEndpoint);
        if (domainsRes.ok) {
          const domainsData = await domainsRes.json();
          setDomains(domainsData.domains || []);
        }
      } catch {
        // Domain fetch is optional — don't block the page
      }
    } catch (err) {
      console.error('Failed to fetch SSO data:', err);
      setError('Failed to connect to API');
    }
    setIsLoading(false);
  }, [authFetch, ssoStatusEndpoint, ssoConfigEndpoint, ssoDomainsEndpoint]);

  useEffect(() => {
    if (authLoading) return;
    fetchSSOData();
  }, [authLoading, fetchSSOData]);

  // Test Connection handler
  const handleTestConnection = async (providerName: string) => {
    setTestingProvider(providerName);
    setTestResult(null);
    setShowTestModal(true);

    try {
      const response = await authFetch(ssoTestEndpoint, {
        method: 'POST',
      });

      if (response.ok) {
        const result = await response.json();
        setTestResult({
          success: true,
          checks: result.checks || {},
        });
      } else {
        const errorData = await response.json();
        setTestResult({
          success: false,
          checks: errorData.detail?.checks || { error: { success: false, error: errorData.detail?.message || 'Test failed' } },
        });
      }
    } catch (err) {
      setTestResult({
        success: false,
        checks: { connection: { success: false, error: 'Failed to connect to API' } },
      });
    }
  };

  // Configure SSO Provider handler
  const handleConfigureProvider = async () => {
    setIsConfiguring(true);
    setConfigError(null);

    try {
      const response = await authFetch(ssoConfigEndpoint, {
        method: 'POST',
        body: JSON.stringify({
          enabled: true,
          provider: providerForm.provider,
          issuer_url: providerForm.issuer_url,
          client_id: providerForm.client_id,
          client_secret: providerForm.client_secret,
          redirect_uri: providerForm.redirect_uri,
          auto_create_users: providerForm.auto_create_users,
          default_role: providerForm.default_role,
          allow_local_auth: providerForm.allow_local_auth,
          force_sso: providerForm.force_sso,
        }),
      });

      if (response.ok) {
        setShowAddProviderModal(false);
        setSuccessMessage(
          isInTenantContext && activeTenant
            ? `SSO configured for ${activeTenant.name}`
            : 'Platform SSO configured'
        );
        setTimeout(() => setSuccessMessage(null), 5000);
        // Reset form
        setProviderForm({
          provider: 'keycloak',
          issuer_url: '',
          client_id: '',
          client_secret: '',
          redirect_uri: window.location.origin + '/api/auth/sso/callback',
          auto_create_users: true,
          default_role: 'user',
          allow_local_auth: true,
          force_sso: false,
        });
        fetchSSOData();
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'object'
          ? errorData.detail.message || JSON.stringify(errorData.detail)
          : errorData.detail || 'Failed to configure SSO';
        setConfigError(errorMessage);
      }
    } catch (err) {
      setConfigError('Failed to configure SSO. Please try again.');
    } finally {
      setIsConfiguring(false);
    }
  };

  // Save Role Mapping handler
  const handleSaveRoleMapping = async () => {
    setIsSavingRoleMapping(true);
    setRoleMappingError(null);

    // Convert array to object, filtering out empty SSO roles
    const mappingObject: Record<string, string> = {};
    roleMapping.forEach(({ ssoRole, platformRole }) => {
      if (ssoRole.trim()) {
        mappingObject[ssoRole.trim()] = platformRole;
      }
    });

    try {
      const response = await authFetch(ssoRoleMappingEndpoint, {
        method: 'PUT',
        body: JSON.stringify(mappingObject),
      });

      if (response.ok) {
        setShowRoleMappingModal(false);
        fetchSSOData();
      } else {
        const errorData = await response.json();
        const errorMessage = typeof errorData.detail === 'object'
          ? errorData.detail.message || JSON.stringify(errorData.detail)
          : errorData.detail || 'Failed to save role mapping';
        setRoleMappingError(errorMessage);
      }
    } catch (err) {
      setRoleMappingError('Failed to save role mapping. Please try again.');
    } finally {
      setIsSavingRoleMapping(false);
    }
  };

  // Add role mapping row
  const addRoleMappingRow = () => {
    setRoleMapping([...roleMapping, { ssoRole: '', platformRole: 'user' }]);
  };

  // Remove role mapping row
  const removeRoleMappingRow = (index: number) => {
    setRoleMapping(roleMapping.filter((_, i) => i !== index));
  };

  // Update role mapping row
  const updateRoleMappingRow = (index: number, field: 'ssoRole' | 'platformRole', value: string) => {
    setRoleMapping(roleMapping.map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    ));
  };

  // Disable SSO handler
  const handleDisableSSO = async () => {
    setIsDisabling(true);
    try {
      const response = await authFetch(ssoDisableEndpoint, { method: 'POST' });
      if (response.ok) {
        setShowDisableConfirm(false);
        setSuccessMessage(
          isInTenantContext && activeTenant
            ? `SSO disabled for ${activeTenant.name}`
            : 'Platform SSO disabled'
        );
        setTimeout(() => setSuccessMessage(null), 5000);
        fetchSSOData();
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to disable SSO');
        setShowDisableConfirm(false);
      }
    } catch {
      setError('Failed to disable SSO. Please try again.');
      setShowDisableConfirm(false);
    } finally {
      setIsDisabling(false);
    }
  };

  // Edit Provider handler — pre-populates form with current config
  const handleEditProvider = async () => {
    try {
      const response = await authFetch(ssoConfigEndpoint);
      if (response.ok) {
        const configData = await response.json();
        setProviderForm({
          provider: configData.provider || 'keycloak',
          issuer_url: configData.issuer_url || '',
          client_id: configData.client_id || '',
          client_secret: '', // Never pre-populate secrets
          redirect_uri: configData.redirect_uri || window.location.origin + '/api/auth/sso/callback',
          auto_create_users: configData.auto_create_users ?? true,
          default_role: configData.default_role || 'user',
          allow_local_auth: configData.allow_local_auth ?? true,
          force_sso: configData.force_sso ?? false,
        });
        setShowAddProviderModal(true);
      } else {
        setError('Failed to load current configuration');
      }
    } catch {
      setError('Failed to load current configuration');
    }
  };

  // Domain CRUD handlers (WQ-071)
  const handleAddDomain = async () => {
    if (!newDomainName.trim()) return;
    setDomainError(null);

    try {
      const body: Record<string, unknown> = { domain: newDomainName.trim() };
      // Admin endpoint also needs tenant_id and can pre-verify
      if (!(isInTenantContext && !userIsSuperAdmin)) {
        if (activeTenant) body.tenant_id = activeTenant.id;
        if (userIsSuperAdmin) body.verified = true;
      }

      const response = await authFetch(ssoDomainsEndpoint, {
        method: 'POST',
        body: JSON.stringify(body),
      });

      if (response.ok) {
        setShowAddDomainModal(false);
        setNewDomainName('');
        fetchSSOData();
        setSuccessMessage(`Domain '${newDomainName.trim()}' registered`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        const errorData = await response.json();
        setDomainError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to add domain');
      }
    } catch {
      setDomainError('Failed to add domain. Please try again.');
    }
  };

  const handleRemoveDomain = async (domainId: string, domainName: string) => {
    if (!confirm(`Remove domain @${domainName}? SSO discovery will stop working for this domain.`)) return;

    try {
      const endpoint = (isInTenantContext && !userIsSuperAdmin)
        ? `/api/tenant-portal/sso/domains/${domainId}`
        : `/api/admin/sso/domains/${domainId}`;
      const response = await authFetch(endpoint, { method: 'DELETE' });
      if (response.ok) {
        fetchSSOData();
        setSuccessMessage(`Domain '${domainName}' removed`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to remove domain');
      }
    } catch {
      setError('Failed to remove domain');
    }
  };

  const handleVerifyDomain = async (domainId: string) => {
    try {
      const response = await authFetch(`/api/admin/sso/domains/${domainId}/verify`, {
        method: 'PUT',
      });
      if (response.ok) {
        fetchSSOData();
        setSuccessMessage('Domain verified');
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to verify domain');
      }
    } catch {
      setError('Failed to verify domain');
    }
  };

  // WQ-204: promote an existing non-primary domain to primary
  const handleSetPrimary = async (domainId: string, domainName: string) => {
    try {
      const response = await authFetch(
        `/api/admin/sso/domains/${domainId}/set-primary`,
        { method: 'POST' },
      );
      if (response.ok) {
        fetchSSOData();
        setSuccessMessage(`Domain '${domainName}' is now primary`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } else {
        const errorData = await response.json();
        setError(typeof errorData.detail === 'string' ? errorData.detail : 'Failed to set primary domain');
      }
    } catch {
      setError('Failed to set primary domain');
    }
  };

  const getStatusBadge = (enabled: boolean) => {
    if (enabled) {
      return <span className="badge badge-success"><CheckCircleIcon size={14} /> Enabled</span>;
    }
    return <span className="badge badge-secondary">Disabled</span>;
  };

  // Context-aware labels (WQ-204: page renamed "SSO & Integration")
  const pageTitle = isInTenantContext
    ? 'Tenant SSO & Integration'
    : 'SSO & Integration';
  const pageSubtitle = isInTenantContext && activeTenant
    ? `Configure SSO and Microsoft Graph integration for ${activeTenant.name}`
    : 'Configure identity providers and external API integrations';

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-left">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <h1 className="page-title">{pageTitle}</h1>
            <TenantContextDropdown />
            {/* Context Indicator Badge */}
            {isInTenantContext && activeTenant ? (
              <span
                className="badge"
                style={{
                  background: activeTenant.branding?.primaryColor || 'var(--primary)',
                  color: '#fff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                <BuildingIcon size={12} />
                {activeTenant.name}
              </span>
            ) : (
              <span
                className="badge"
                style={{
                  background: 'rgba(59, 130, 246, 0.15)',
                  color: '#3b82f6',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '4px 10px',
                  fontSize: '12px',
                  fontWeight: 500,
                }}
              >
                Platform Level
              </span>
            )}
          </div>
          <p className="page-subtitle">{pageSubtitle}</p>
        </div>
        <div className="page-header-right">
          <button className="btn btn-ghost" onClick={fetchSSOData}>
            <RefreshIcon size={18} />
          </button>
        </div>
      </div>

      {/* Tenant context info banner */}
      {isInTenantContext && activeTenant && (
        <div style={{
          padding: '12px 16px',
          background: 'rgba(14, 165, 233, 0.08)',
          border: '1px solid rgba(14, 165, 233, 0.2)',
          borderRadius: '8px',
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          marginBottom: '20px',
        }}>
          <BuildingIcon style={{ color: 'rgb(14, 165, 233)', marginTop: '2px', flexShrink: 0 }} />
          <div>
            <strong>Tenant SSO Configuration</strong> — Configure a dedicated identity provider for {activeTenant.name}.
            If no tenant-specific SSO is configured, the platform-level SSO provider will be used as fallback.
          </div>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CheckCircleIcon size={18} />
          <span>{successMessage}</span>
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setSuccessMessage(null)}
            style={{ marginLeft: 'auto' }}
          >
            ×
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="glass-card">
          <div className="loading-state">
            <div className="spinner" />
            <p>Loading SSO configuration...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-6">
          {/* SSO Status */}
          <div className="glass-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="card-title">SSO Status</h3>
              {getStatusBadge(status?.enabled || false)}
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-secondary">Provider</span>
                <span className="font-medium">{status?.provider || 'Not configured'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-secondary">Local Auth Allowed</span>
                <span className="font-medium">{status?.allow_local_auth ? 'Yes' : 'No'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-secondary">Force SSO</span>
                <span className="font-medium">{status?.force_sso ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="glass-card">
            <h3 className="card-title mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                className="btn btn-primary w-full"
                onClick={() => {
                  if (status?.enabled) {
                    handleEditProvider();
                  } else {
                    setShowAddProviderModal(true);
                  }
                }}
                title={isInTenantContext ? 'Configure SSO for this tenant' : 'Configure SSO provider'}
              >
                <SettingsIcon size={16} />
                {status?.enabled ? 'Edit Provider' : 'Add Provider'}
              </button>
              <button
                className="btn btn-secondary w-full"
                onClick={() => setShowRoleMappingModal(true)}
                title={isInTenantContext ? 'Configure role mapping for this tenant' : 'Configure role mapping'}
              >
                <KeyIcon size={16} />
                Configure Role Mapping
              </button>
              {status?.enabled && (
                <button
                  className="btn btn-danger w-full"
                  onClick={() => setShowDisableConfirm(true)}
                  title="Disable SSO — users will fall back to local authentication"
                >
                  <AlertIcon size={16} />
                  Disable SSO
                </button>
              )}
            </div>
          </div>

          {/* Configured Provider */}
          {status?.enabled && status?.provider && (
            <div className="glass-card col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h3 className="card-title">Active Provider</h3>
                {getStatusBadge(true)}
              </div>
              <div className="provider-card active" style={{ maxWidth: '400px' }}>
                <div className="provider-icon">
                  <KeyIcon size={24} />
                </div>
                <span className="font-medium">{status.provider}</span>
                <span className="text-secondary text-sm">OpenID Connect</span>
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => handleTestConnection(status.provider!)}
                    disabled={testingProvider === status.provider}
                  >
                    {testingProvider === status.provider ? (
                      <><span className="spinner" style={{ width: '14px', height: '14px' }} /> Testing...</>
                    ) : (
                      <><PlayIcon size={14} /> Test</>
                    )}
                  </button>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={handleEditProvider}
                  >
                    <SettingsIcon size={14} /> Edit
                  </button>
                  <button
                    className="btn btn-ghost btn-sm text-error"
                    onClick={() => setShowDisableConfirm(true)}
                  >
                    Disable
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Available Providers */}
          <div className="glass-card col-span-2">
            <h3 className="card-title mb-4">Available SSO Identity Providers</h3>
            {providers.length === 0 ? (
              <div className="empty-state">
                <KeyIcon size={48} />
                <p>No SSO providers available</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                {providers.map((provider) => (
                  <div
                    key={provider.id}
                    className={`provider-card ${status?.provider?.toLowerCase() === provider.id ? 'active' : ''}`}
                    onClick={() => {
                      setProviderForm(prev => ({ ...prev, provider: provider.id }));
                      setShowAddProviderModal(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title={`Configure ${provider.name}`}
                  >
                    <div className="provider-icon">
                      <KeyIcon size={24} />
                    </div>
                    <span className="font-medium">{provider.name}</span>
                    <span className="text-secondary text-sm">{provider.description}</span>
                    {provider.discovery_supported && (
                      <span className="badge badge-info mt-2" style={{ fontSize: '10px' }}>
                        Auto-discovery
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* WQ-204: Microsoft Graph Configuration card.
              Lives between SSO providers and Supported Protocols so the
              two Microsoft credential stores read top-to-bottom in
              architectural order: SSO (per-user) → Graph (per-app). */}
          <div className="glass-card col-span-2">
            <MicrosoftGraphConfigCard
              authFetch={authFetch}
              tenantId={isInTenantContext && activeTenant ? activeTenant.id : null}
              tenantLabel={isInTenantContext && activeTenant ? activeTenant.name : undefined}
            />
          </div>

          {/* Supported Protocols Info */}
          <div className="glass-card col-span-2">
            <h3 className="card-title mb-4">Supported Protocols</h3>
            <div className="grid grid-cols-2 gap-4">
              {['OAuth 2.0', 'OpenID Connect (OIDC)'].map((protocol) => (
                <div key={protocol} className="protocol-card">
                  <KeyIcon size={20} />
                  <span className="font-medium">{protocol}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Domain Mappings (WQ-071) — show when SSO is configured */}
          {status?.enabled && (
            <div className="glass-card col-span-2">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <h3 className="card-title" style={{ margin: 0 }}>Domain Mappings</h3>
                <button className="btn btn-primary btn-sm" onClick={() => { setShowAddDomainModal(true); setDomainError(null); setNewDomainName(''); }}>
                  <PlusIcon size={14} /> Add Domain
                </button>
              </div>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                Users with email addresses matching these domains will be automatically
                routed to this SSO provider on the login page.
              </p>

              {domains.length === 0 ? (
                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <p>No domains registered. Add a domain to enable email-based SSO discovery.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {domains.map((d) => (
                    <div key={d.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px',
                      border: '1px solid var(--border)',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="font-medium">@{d.domain}</span>
                        {d.verified ? (
                          <span className="badge badge-success"><CheckCircleIcon size={12} /> Verified</span>
                        ) : (
                          <span className="badge badge-warning">Unverified</span>
                        )}
                        {d.is_primary && (
                          <span className="badge badge-info">Primary</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {!d.verified && userIsSuperAdmin && (
                          <button className="btn btn-ghost btn-sm" onClick={() => handleVerifyDomain(d.id)}>
                            <CheckIcon size={14} /> Verify
                          </button>
                        )}
                        {!d.is_primary && userIsSuperAdmin && (
                          <button
                            className="btn btn-ghost btn-sm"
                            data-testid={`set-primary-${d.domain}`}
                            onClick={() => handleSetPrimary(d.id, d.domain)}
                          >
                            Set Primary
                          </button>
                        )}
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--error)' }} onClick={() => handleRemoveDomain(d.id, d.domain)}>
                          <TrashIcon size={14} /> Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Add Domain Modal (WQ-071) */}
      {showAddDomainModal && (
        <div className="modal-overlay" onClick={() => setShowAddDomainModal(false)}>
          <div className="modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Add Domain</h2>
              <button className="btn btn-ghost" onClick={() => setShowAddDomainModal(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px', fontSize: '14px' }}>
                Register an email domain for SSO discovery. Users with this email domain
                will be prompted to sign in via SSO on the login page.
              </p>
              {domainError && (
                <div className="alert alert-error" style={{ marginBottom: '12px' }}>{domainError}</div>
              )}
              <div className="form-group">
                <label className="input-label">Email Domain</label>
                <input
                  type="text"
                  className="input"
                  placeholder="company.com"
                  value={newDomainName}
                  onChange={(e) => setNewDomainName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                  autoFocus
                />
                <span style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px', display: 'block' }}>
                  {!userIsSuperAdmin && 'Domain will require admin verification before SSO routing is active.'}
                  {userIsSuperAdmin && 'As a super admin, the domain will be ready for SSO routing immediately.'}
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowAddDomainModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddDomain} disabled={!newDomainName.trim()}>
                Add Domain
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Connection Modal */}
      {showTestModal && (
        <div className="modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="modal" style={{ maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Connection Test Results</h2>
              <button className="btn btn-ghost" onClick={() => setShowTestModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {testingProvider && !testResult && (
                <div className="loading-state">
                  <div className="spinner" />
                  <p>Testing connection to {testingProvider}...</p>
                </div>
              )}
              {testResult && (
                <div>
                  <div
                    className={`alert ${testResult.success ? 'alert-success' : 'alert-error'}`}
                    style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}
                  >
                    {testResult.success ? (
                      <>
                        <CheckCircleIcon size={18} />
                        <span><strong>Connection Successful!</strong> All checks passed.</span>
                      </>
                    ) : (
                      <>
                        <AlertIcon size={18} />
                        <span><strong>Connection Failed.</strong> See details below.</span>
                      </>
                    )}
                  </div>

                  <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px' }}>Check Results:</h4>
                  <div className="space-y-2">
                    {Object.entries(testResult.checks).map(([check, result]) => (
                      <div
                        key={check}
                        style={{
                          padding: '12px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '8px',
                          border: `1px solid ${result.success ? 'var(--success)' : 'var(--error)'}`,
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span className="font-medium" style={{ textTransform: 'capitalize' }}>{check}</span>
                          {result.success ? (
                            <span className="badge badge-success"><CheckCircleIcon size={12} /> Passed</span>
                          ) : (
                            <span className="badge badge-error"><AlertIcon size={12} /> Failed</span>
                          )}
                        </div>
                        {result.endpoints_found !== undefined && (
                          <p className="text-secondary text-sm mt-1">Endpoints found: {result.endpoints_found}</p>
                        )}
                        {result.keys_found !== undefined && (
                          <p className="text-secondary text-sm mt-1">Keys found: {result.keys_found}</p>
                        )}
                        {result.error && (
                          <p className="text-error text-sm mt-1">{result.error}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowTestModal(false)}>
                Close
              </button>
              {testResult && !testResult.success && (
                <button className="btn btn-primary" onClick={() => handleTestConnection(testingProvider!)}>
                  Retry Test
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add Provider Modal */}
      {showAddProviderModal && (
        <div className="modal-overlay" onClick={() => setShowAddProviderModal(false)}>
          <div
            className="modal"
            style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{status?.enabled ? 'Update SSO Provider' : 'Configure SSO Provider'}</h2>
              <button className="btn btn-ghost" onClick={() => setShowAddProviderModal(false)}>×</button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              {configError && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  {configError}
                </div>
              )}

              {/* Provider Selection */}
              <div className="form-group">
                <label>Identity Provider</label>
                <select
                  className="select"
                  value={providerForm.provider}
                  onChange={(e) => setProviderForm({ ...providerForm, provider: e.target.value })}
                >
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* OIDC Configuration */}
              <div style={{ marginTop: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  OIDC Configuration
                </h4>

                <div className="form-group">
                  <label>Issuer URL *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="https://your-idp.com/realms/your-realm"
                    value={providerForm.issuer_url}
                    onChange={(e) => setProviderForm({ ...providerForm, issuer_url: e.target.value })}
                  />
                  <small className="text-secondary">The OIDC issuer URL. Endpoints will be auto-discovered.</small>
                </div>

                <div className="form-group">
                  <label>Client ID *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="your-client-id"
                    value={providerForm.client_id}
                    onChange={(e) => setProviderForm({ ...providerForm, client_id: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Client Secret {status?.enabled ? '' : '*'}</label>
                  <input
                    type="password"
                    className="input"
                    placeholder={status?.enabled ? '••••••••• (leave blank to keep current)' : 'your-client-secret'}
                    value={providerForm.client_secret}
                    onChange={(e) => setProviderForm({ ...providerForm, client_secret: e.target.value })}
                  />
                  {status?.enabled && (
                    <small className="text-secondary">Leave blank to keep the existing secret.</small>
                  )}
                </div>

                <div className="form-group">
                  <label>Redirect URI *</label>
                  <input
                    type="text"
                    className="input"
                    placeholder="https://your-app.com/api/auth/sso/callback"
                    value={providerForm.redirect_uri}
                    onChange={(e) => setProviderForm({ ...providerForm, redirect_uri: e.target.value })}
                  />
                </div>
              </div>

              {/* Behavior Settings */}
              <div style={{ marginTop: '24px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '12px', color: 'var(--text-secondary)' }}>
                  Behavior Settings
                </h4>

                <div className="form-group">
                  <label>Default Role for New Users</label>
                  <select
                    className="select"
                    value={providerForm.default_role}
                    onChange={(e) => setProviderForm({ ...providerForm, default_role: e.target.value })}
                  >
                    {PLATFORM_ROLES.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={providerForm.auto_create_users}
                      onChange={(e) => setProviderForm({ ...providerForm, auto_create_users: e.target.checked })}
                    />
                    <span>Auto-create users</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={providerForm.allow_local_auth}
                      onChange={(e) => setProviderForm({ ...providerForm, allow_local_auth: e.target.checked })}
                    />
                    <span>Allow local auth</span>
                  </label>

                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={providerForm.force_sso}
                      onChange={(e) => setProviderForm({ ...providerForm, force_sso: e.target.checked })}
                    />
                    <span>Force SSO login</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddProviderModal(false)}
                disabled={isConfiguring}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleConfigureProvider}
                disabled={isConfiguring || !providerForm.issuer_url || !providerForm.client_id || (!status?.enabled && !providerForm.client_secret)}
              >
                {isConfiguring ? (
                  <>
                    <span className="spinner" style={{ width: '14px', height: '14px' }} />
                    Configuring...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Save Configuration
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Disable SSO Confirmation */}
      {showDisableConfirm && (
        <div className="modal-overlay" onClick={() => setShowDisableConfirm(false)}>
          <div className="modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Disable SSO</h2>
              <button className="btn btn-ghost" onClick={() => setShowDisableConfirm(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="alert alert-warning" style={{ marginBottom: '1rem', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                <AlertIcon size={18} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong>Warning:</strong> Disabling SSO will prevent users from signing in with their identity provider.
                  {isInTenantContext && activeTenant ? (
                    <> Users for <strong>{activeTenant.name}</strong> will fall back to {' '}
                      platform-level SSO (if configured) or local authentication.</>
                  ) : (
                    <> All tenants without their own SSO config will fall back to local authentication.</>
                  )}
                </div>
              </div>
              <p className="text-secondary" style={{ fontSize: '14px' }}>
                This does not delete the configuration — you can re-enable SSO later without reconfiguring.
                Users who are currently logged in will not be affected until their session expires.
              </p>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDisableConfirm(false)}
                disabled={isDisabling}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDisableSSO}
                disabled={isDisabling}
              >
                {isDisabling ? (
                  <>
                    <span className="spinner" style={{ width: '14px', height: '14px' }} />
                    Disabling...
                  </>
                ) : (
                  'Disable SSO'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Mapping Modal */}
      {showRoleMappingModal && (
        <div className="modal-overlay" onClick={() => setShowRoleMappingModal(false)}>
          <div
            className="modal"
            style={{ maxWidth: '600px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Configure Role Mapping</h2>
              <button className="btn btn-ghost" onClick={() => setShowRoleMappingModal(false)}>×</button>
            </div>

            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              {roleMappingError && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                  {roleMappingError}
                </div>
              )}

              <p className="text-secondary" style={{ marginBottom: '16px' }}>
                Map SSO roles/groups to platform roles. Users will be assigned the mapped platform role based on their SSO claims.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {roleMapping.map((mapping, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr auto 1fr auto',
                      gap: '12px',
                      alignItems: 'center',
                      padding: '12px',
                      background: 'var(--bg-secondary)',
                      borderRadius: '8px',
                      border: '1px solid var(--border)',
                    }}
                  >
                    <input
                      type="text"
                      className="input"
                      placeholder="SSO role/group (e.g., admin)"
                      value={mapping.ssoRole}
                      onChange={(e) => updateRoleMappingRow(index, 'ssoRole', e.target.value)}
                    />
                    <span className="text-secondary">→</span>
                    <select
                      className="select"
                      value={mapping.platformRole}
                      onChange={(e) => updateRoleMappingRow(index, 'platformRole', e.target.value)}
                    >
                      {PLATFORM_ROLES.map((role) => (
                        <option key={role} value={role}>{role}</option>
                      ))}
                    </select>
                    <button
                      className="btn btn-ghost btn-sm text-error"
                      onClick={() => removeRoleMappingRow(index)}
                      disabled={roleMapping.length === 1}
                      title="Remove mapping"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              <button
                className="btn btn-ghost"
                style={{ marginTop: '12px' }}
                onClick={addRoleMappingRow}
              >
                <PlusIcon size={14} />
                Add Mapping
              </button>

              <div
                className="alert alert-info"
                style={{ marginTop: '16px', display: 'flex', alignItems: 'flex-start', gap: '8px' }}
              >
                <AlertIcon size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                <div style={{ fontSize: '13px' }}>
                  <strong>Note:</strong> If no mapping matches, users will be assigned the default role configured for the SSO provider.
                  Leave SSO role empty to skip a row.
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowRoleMappingModal(false)}
                disabled={isSavingRoleMapping}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveRoleMapping}
                disabled={isSavingRoleMapping}
              >
                {isSavingRoleMapping ? (
                  <>
                    <span className="spinner" style={{ width: '14px', height: '14px' }} />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon size={16} />
                    Save Mappings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SSOPage;
